import { useState, useCallback, useMemo } from "react";
import { useDevAgentStore } from "@/stores/devAgentStore";
import { useShallow } from "zustand/react/shallow";
import {
  deriveWorkspaceArtifactFiles,
  deriveImpactPanelItems,
  deriveTestPanelItems,
} from "./rightPanels/derivePanelData";
import { SearchPanel } from "./rightPanels/SearchPanel";
import {
  PanelLeftClose,
  PanelLeftOpen,
  ListChecks,
  BookOpen,
} from "lucide-react";
import { PanelSection } from "./rightPanels/PanelSection";
import { DependenciesPanel } from "./rightPanels/DependenciesPanel";
import { AgentsPanel } from "./rightPanels/AgentsPanel";
import { ConversationSummaryInner } from "./rightPanels/ConversationSummaryInner";
import { DocsSearchPanel } from "./rightPanels/DocsSearchPanel";
import { ImpactPanel } from "./rightPanels/ImpactPanel";
import { TestsPanel } from "./rightPanels/TestsPanel";
import { WorkspacePanel } from "./rightPanels/WorkspacePanel";
import type { RightPanelsProps } from "./rightPanels/rightPanelsTypes";

export type { RightPanelsProps } from "./rightPanels/rightPanelsTypes";
export { PanelSection } from "./rightPanels/PanelSection";

export function RightPanels({
  onSendMessage,
  onApprove: _onApprove,
  onOpenFileInEditor,
  onOpenCodeChange: _onOpenCodeChange,
  rightPanelWidth,
  analyzingFile,
  isBusy,
  collapsed,
  onToggleCollapse,
  sessionUuid,
  onDocsPostedToChat,
}: RightPanelsProps) {
  const { feedItems, pinnedArtifactIds, artifactBadges, artifactsById, storeTestRuns } = useDevAgentStore(
    useShallow((s) => ({
      feedItems: s.feedItems,
      pinnedArtifactIds: s.pinnedArtifactIds,
      artifactBadges: s.artifactBadges,
      artifactsById: s.artifactsById,
      storeTestRuns: s.testRuns,
    }))
  );

  // Panel order — user can move panels up/down (in-memory only).
  // "docs" = standalone Salesforce docs search, independent from the agent.
  const [panelOrder, setPanelOrder] = useState<string[]>([
    "search", "agents", "artifacts", "impact", "docs", "dependencies", "tests", "summary",
  ]);
  const movePanel = useCallback((id: string, dir: "up" | "down") => {
    setPanelOrder((prev) => {
      const idx = prev.indexOf(id);
      if (idx < 0) return prev;
      const swap = dir === "up" ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  }, []);

  // Clear handlers — remove items from feed (local only, not persisted)
  const clearArtifacts = useCallback(() => {
    const state = useDevAgentStore.getState();
    state.feedItems.filter((f) => f.type === "tool_call" && f.data.status === "done").forEach((f) => state.removeFeedItem(f.id));
  }, []);
  const clearImpact = useCallback(() => {
    const state = useDevAgentStore.getState();
    state.feedItems.filter((f) => f.type === "impact_analysis" || (f.type === "tool_call" && (f.data.name?.includes("impact") || f.data.name?.includes("graph")))).forEach((f) => state.removeFeedItem(f.id));
  }, []);
  const clearTests = useCallback(() => {
    const state = useDevAgentStore.getState();
    state.feedItems.filter((f) => f.type === "test_coverage" || (f.type === "tool_call" && (f.data.name?.includes("test") || f.data.name?.includes("coverage") || (f.data.name === "tooling_api" && f.data.args?.action === "run_tests")))).forEach((f) => state.removeFeedItem(f.id));
  }, []);
  const clearSummary = useCallback(() => {
    useDevAgentStore.getState().setConversationSummary([]);
  }, []);

  const artifactFiles = useMemo(
    () => deriveWorkspaceArtifactFiles(feedItems, pinnedArtifactIds, artifactsById),
    [feedItems, pinnedArtifactIds, artifactsById],
  );
  const impactItems = useMemo(() => deriveImpactPanelItems(feedItems), [feedItems]);
  const feedTestItems = useMemo(() => deriveTestPanelItems(feedItems), [feedItems]);

  // Merge agent-driven test items (from feed) with user-initiated ones (from store)
  // into a single panel list. Panel dedupes by className below and keeps most recent.
  const testItems = useMemo(() => {
    const fromStore = storeTestRuns.map((tr) => {
      const primary =
        tr.coveredClasses?.find((coveredClass) => coveredClass.name === tr.className) ||
        tr.coveredClasses?.[0];
      return {
        id: `storeTest-${tr.testClassName}-${tr.ts}`,
        type: "test_coverage" as const,
        timestamp: tr.ts,
        data: {
          // className == testClassName so Re-run button passes the right value
          className: tr.testClassName,
          class_name: tr.testClassName,
          productionClass: tr.className,
          summary: tr.summary,
          raw_result: tr.rawResult,
          success: tr.success,
          coveragePct: primary?.coverage_pct ?? undefined,
          uncoveredCount: primary?.num_uncovered,
          coveredClasses: tr.coveredClasses,
          status: tr.success ? "passed" : (tr.total ? "failed" : undefined),
        } as Record<string, any>,
      };
    });
    return [...feedTestItems, ...fromStore];
  }, [feedTestItems, storeTestRuns]);

  if (collapsed) {
    return (
      <div className="w-10 border-l border-border flex flex-col items-center pt-2 shrink-0">
        <button
          onClick={onToggleCollapse}
          className="p-1.5 rounded hover:bg-muted/50 transition-colors"
          title="Expand panels"
        >
          <PanelLeftOpen className="w-4 h-4 text-muted-foreground rotate-180" />
        </button>
      </div>
    );
  }

  return (
    <div
      className="h-full min-h-0 border-l border-border flex flex-col shrink-0 overflow-hidden text-foreground text-xs"
      style={{ width: rightPanelWidth, minWidth: 200, maxWidth: 500 }}
    >
      {/* Collapse button — outside scroll so the rail header stays visible */}
      <div className="flex items-center justify-between px-2 py-1 border-b border-border shrink-0 bg-background z-10">
        <span className="text-[10px] font-semibold text-foreground/85 uppercase tracking-wide">
          Panels
        </span>
        <button
          onClick={onToggleCollapse}
          className="p-1 rounded hover:bg-muted/50 transition-colors"
          title="Collapse panels"
        >
          <PanelLeftClose className="w-3.5 h-3.5 text-muted-foreground rotate-180" />
        </button>
      </div>
      <div
        className="flex-1 min-h-0 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/25 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/45"
      >
      {/* Panels rendered in user-customizable order with clear/move controls */}
      {panelOrder.map((panelId, panelIdx) => {
        const isFirst = panelIdx === 0;
        const isLast = panelIdx === panelOrder.length - 1;
        const up = !isFirst ? () => movePanel(panelId, "up") : undefined;
        const dn = !isLast ? () => movePanel(panelId, "down") : undefined;

        if (panelId === "docs") {
          return (
            <PanelSection
              key="docs"
              disabled={false}
              title="Salesforce Docs"
              icon={<BookOpen className="w-3.5 h-3.5" />}
              count={0}
              onMoveUp={up}
              onMoveDown={dn}
            >
              <DocsSearchPanel
                sessionUuid={sessionUuid ?? null}
                onMessagePosted={onDocsPostedToChat}
              />
            </PanelSection>
          );
        }
        if (panelId === "search") return <SearchPanel key="search" onOpenFileInEditor={onOpenFileInEditor} />;
        if (panelId === "agents") return <AgentsPanel key="agents" />;
        if (panelId === "summary") {
          const summaryCount = feedItems.filter((f) => f.type === "chat_user" && (f.data.content?.length ?? 0) >= 3).length;
          return (
            <PanelSection
              key="summary"
              disabled={isBusy}
              title="Summary"
              icon={<ListChecks className="w-3.5 h-3.5" />}
              count={summaryCount}
              onClear={clearSummary}
              onMoveUp={up}
              onMoveDown={dn}
            >
              <ConversationSummaryInner />
            </PanelSection>
          );
        }

        // "plan" panel removed — plans now render inline in the chat feed

        if (panelId === "artifacts") {
          return (
            <WorkspacePanel
              key="artifacts"
              artifactFiles={artifactFiles}
              artifactBadges={artifactBadges}
              feedItems={feedItems}
              isBusy={isBusy}
              onClear={clearArtifacts}
              onMoveUp={up}
              onMoveDown={dn}
              onOpenFileInEditor={onOpenFileInEditor}
            />
          );
        }

        if (panelId === "impact") {
          return (
            <ImpactPanel
              key="impact"
              impactItems={impactItems}
              analyzingFile={analyzingFile}
              isBusy={isBusy}
              onClear={clearImpact}
              onMoveUp={up}
              onMoveDown={dn}
              onSendMessage={onSendMessage}
              onOpenFileInEditor={onOpenFileInEditor}
            />
          );
        }

        if (panelId === "dependencies") {
          return (
            <DependenciesPanel
              key="dependencies"
              disabled={isBusy}
              onMoveUp={up}
              onMoveDown={dn}
              onSendMessage={onSendMessage}
              onOpenFileInEditor={onOpenFileInEditor}
            />
          );
        }

        if (panelId === "tests") {
          return (
            <TestsPanel
              key="tests"
              testItems={testItems}
              analyzingFile={analyzingFile}
              isBusy={isBusy}
              onClear={clearTests}
              onMoveUp={up}
              onMoveDown={dn}
              onSendMessage={onSendMessage}
              onOpenFileInEditor={onOpenFileInEditor}
            />
          );
        }

        return null; // unknown panel id
      })}
      </div>
    </div>
  );
}
